import { httpService } from "../services/HttpService";
import type {
  LoginRequest,
  LoginResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ChangeExpiredPasswordRequest,
  ChangeExpiredPasswordResponse,
  ApiResponse,
} from "../types/auth.type";
import type { User } from "../types/Sidebar.type";

export interface IAuthRepository {
  login(credentials: LoginRequest): Promise<LoginResponse>;
  logout(): Promise<void>;
  refreshToken(refreshToken: string): Promise<LoginResponse>;
  validateToken(token: string): Promise<boolean>;
  forgotPassword(
    request: ForgotPasswordRequest,
  ): Promise<ForgotPasswordResponse>;
  resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse>;
  changePassword(
    request: ChangePasswordRequest,
  ): Promise<ChangePasswordResponse>;
  changeExpiredPassword(
    request: ChangeExpiredPasswordRequest,
  ): Promise<ChangeExpiredPasswordResponse>;
}

export class AuthRepository implements IAuthRepository {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: ApiResponse<LoginResponse> = await httpService.post<
      LoginResponse,
      LoginRequest
    >("/auth/login", credentials);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error((response.error ?? response.message) || "Login failed");
    }

    if (!response.data) {
      throw new Error(
        "Login succeeded but no user data was returned from the server.",
      );
    }

    // Map backend user data to frontend User type
    const mappedUser: User = {
      id: response.data.user.id,
      username:
        (response.data.user as { username?: string }).username ??
        credentials.email.split("@")[0] ??
        "user",
      roles: response.data.user.roles,
      tenantId: response.data.user.tenantId,
      isAdmin: response.data.user.isAdmin,
      lastName: "",
      firstName: "",
    };

    return {
      ...response.data,
      user: mappedUser,
    };
  }

  async logout(): Promise<void> {
    await httpService.post("/auth/logout");
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const response: ApiResponse<LoginResponse> = await httpService.post<
      LoginResponse,
      { token: string }
    >("/auth/refresh", { token: refreshToken });

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Token refresh failed",
      );
    }

    if (!response.data) {
      throw new Error(
        "Token refresh succeeded but no data was returned from the server.",
      );
    }

    return response.data;
  }

  async validateToken(token: string): Promise<boolean> {
    void token;
    try {
      const response: ApiResponse<{ valid: boolean }> =
        await httpService.get("/auth/validate");

      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

      if (!isSuccess) {
        return false;
      }

      return response.data?.valid || false;
    } catch {
      return false;
    }
  }

  async forgotPassword(
    request: ForgotPasswordRequest,
  ): Promise<ForgotPasswordResponse> {
    const response: ApiResponse<ForgotPasswordResponse> =
      await httpService.post<ForgotPasswordResponse, ForgotPasswordRequest>(
        "/auth/forgotpassword",
        request,
      );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to send password reset instructions",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no response data was returned from the server.",
      );
    }

    return response.data;
  }

  async resetPassword(
    request: ResetPasswordRequest,
  ): Promise<ResetPasswordResponse> {
    const response: ApiResponse<ResetPasswordResponse> = await httpService.post<
      ResetPasswordResponse,
      ResetPasswordRequest
    >("/auth/resetpassword", request);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to reset password",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no response data was returned from the server.",
      );
    }

    return response.data;
  }

  async changePassword(
    request: ChangePasswordRequest,
  ): Promise<ChangePasswordResponse> {
    const response: ApiResponse<ChangePasswordResponse> =
      await httpService.post<ChangePasswordResponse, ChangePasswordRequest>(
        "/auth/change-password",
        request,
      );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to change password",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no response data was returned from the server.",
      );
    }

    return response.data;
  }

  async changeExpiredPassword(
    request: ChangeExpiredPasswordRequest,
  ): Promise<ChangeExpiredPasswordResponse> {
    const response: ApiResponse<ChangeExpiredPasswordResponse> =
      await httpService.post<
        ChangeExpiredPasswordResponse,
        ChangeExpiredPasswordRequest
      >("/auth/change-expired-password", request);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to change expired password",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no response data was returned from the server.",
      );
    }

    return response.data;
  }
}

// Singleton instance
export const authRepository = new AuthRepository();
